import { baseApi } from './baseApi';
import {
  DashboardDto,
  DashboardQueryDto,
  RestaurantOperationalStatusDto,
  RealTimeMetricsDto,
  FinancialSummaryDto,
  PerformanceKpisDto,
  HourlySalesDto,
  PaymentMethodBreakdownDto,
} from '../types/analytics';

export const analyticsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Dashboard endpoints
    getDashboard: builder.query<DashboardDto, { restaurantId: string; query?: DashboardQueryDto }>({
      query: ({ restaurantId, query = {} }) => ({
        url: `/restaurants/${restaurantId}/analytics/dashboard`,
        params: query,
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Analytics' as const, id: `dashboard-${restaurantId}` },
      ],
    }),

    getOperationalStatus: builder.query<RestaurantOperationalStatusDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/analytics/operational-status`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Analytics' as const, id: `status-${restaurantId}` },
      ],
    }),

    getRealTimeMetrics: builder.query<RealTimeMetricsDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/analytics/real-time-metrics`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Analytics' as const, id: `metrics-${restaurantId}` },
      ],
    }),

    getFinancialSummary: builder.query<
      FinancialSummaryDto,
      { restaurantId: string; fromDate?: string; toDate?: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/analytics/financial-summary`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Analytics' as const, id: `financial-${restaurantId}` },
      ],
    }),

    getPerformanceKpis: builder.query<
      PerformanceKpisDto,
      { restaurantId: string; fromDate?: string; toDate?: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/analytics/performance-kpis`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Analytics' as const, id: `kpis-${restaurantId}` },
      ],
    }),

    getHourlySales: builder.query<
      HourlySalesDto[],
      { restaurantId: string; date: string }
    >({
      query: ({ restaurantId, date }) => ({
        url: `/restaurants/${restaurantId}/analytics/hourly-sales`,
        params: { date },
      }),
      providesTags: (result, error, { restaurantId, date }) => [
        { type: 'Analytics' as const, id: `hourly-${restaurantId}-${date}` },
      ],
    }),

    getPaymentMethodBreakdown: builder.query<
      PaymentMethodBreakdownDto[],
      { restaurantId: string; fromDate?: string; toDate?: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/analytics/payment-methods`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Analytics' as const, id: `payments-${restaurantId}` },
      ],
    }),

    getRevenue: builder.query<
      { revenue: number; fromDate: string; toDate: string },
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/analytics/revenue`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Analytics' as const, id: `revenue-${restaurantId}` },
      ],
    }),

    getCashOnHand: builder.query<
      { cashOnHand: number; timestamp: string },
      string
    >({
      query: (restaurantId) => `/restaurants/${restaurantId}/analytics/cash-on-hand`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Analytics' as const, id: `cash-${restaurantId}` },
      ],
    }),

    // Real-time polling hook
    getDashboardPolling: builder.query<DashboardDto, { restaurantId: string; query?: DashboardQueryDto }>({
      query: ({ restaurantId, query = {} }) => ({
        url: `/restaurants/${restaurantId}/analytics/dashboard`,
        params: query,
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Analytics' as const, id: `dashboard-polling-${restaurantId}` },
      ],
    }),

    getOperationalStatusPolling: builder.query<RestaurantOperationalStatusDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/analytics/operational-status`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Analytics' as const, id: `status-polling-${restaurantId}` },
      ],
    }),

    getRealTimeMetricsPolling: builder.query<RealTimeMetricsDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/analytics/real-time-metrics`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Analytics' as const, id: `metrics-polling-${restaurantId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDashboardQuery,
  useGetOperationalStatusQuery,
  useGetRealTimeMetricsQuery,
  useGetFinancialSummaryQuery,
  useGetPerformanceKpisQuery,
  useGetHourlySalesQuery,
  useGetPaymentMethodBreakdownQuery,
  useGetRevenueQuery,
  useGetCashOnHandQuery,
  
  // Polling hooks for real-time updates
  useGetDashboardPollingQuery,
  useGetOperationalStatusPollingQuery,
  useGetRealTimeMetricsPollingQuery,
} = analyticsApi;